'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { Sparkles, X } from 'lucide-react'
import { useTranslation } from '@/lib/i18n/use-translation'

// Sitewide announcement strip, pinned above the header. Nosto-inspired:
// gradient background, single line, dismissible. Persists dismissal in
// localStorage by `storageKey` so the same banner doesn't re-surface
// for a returning visitor; bumping the key (e.g. ANNOUNCEMENT_KEY in
// app/layout.tsx) re-shows the new banner to everyone.

type AnnouncementProps = {
  storageKey: string
  eyebrow: string
  body: React.ReactNode
  href: string
  cta?: string
}

export function AnnouncementBar({ storageKey, eyebrow, body, href, cta }: AnnouncementProps) {
  const { t } = useTranslation()
  const eyebrowText = eyebrow || t.announce.new
  const ctaText = cta || t.announce.cta
  const [dismissed, setDismissed] = useState(true)

  useEffect(() => {
    try {
      setDismissed(window.localStorage.getItem(storageKey) === '1')
    } catch {
      setDismissed(false)
    }
  }, [storageKey])

  if (dismissed) return null

  return (
    <div className="relative isolate overflow-hidden bg-gradient-to-r from-primary via-rose-500 to-amber-500 text-primary-foreground">
      <div className="container-wide flex items-center justify-between gap-4 py-2 text-[12px] sm:text-[13px]">
        <Link href={href} className="group flex min-w-0 items-center gap-2.5">
          <Sparkles className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="shrink-0 rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.14em]">
            {eyebrowText}
          </span>
          <span className="truncate">{body}</span>
          <span className="hidden shrink-0 items-center gap-1 font-semibold underline-offset-4 group-hover:underline sm:inline-flex">
            {ctaText} →
          </span>
        </Link>
        <button
          type="button"
          onClick={() => {
            try { window.localStorage.setItem(storageKey, '1') } catch {}
            setDismissed(true)
          }}
          className="-mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white/80 transition hover:bg-white/10 hover:text-white"
          aria-label="Dismiss announcement"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  )
}
